<script lang="ts" setup>
import { computed, onBeforeMount, ref } from "vue";
import { onClickOutside } from "@vueuse/core";

// Utils
import { useI18n } from "vue-i18n";
import { getCurrencySymbol } from "~/utils/currency";

import { formatNumberLocal } from "~/utils/math";

// Stores
import usePOLPrice from "~/stores/POLprice";

// Types
import { Currency } from "fungi-types";
import { PurchaseInfo } from "./ModalBid.vue";

const showCurrencyDropdown = ref(false);

const currencyDropdown = ref(null);

onClickOutside(currencyDropdown, () => {
  showCurrencyDropdown.value = false;
});

const { t } = useI18n();
const polPrice = usePOLPrice();

const props = defineProps<{
  purchaseInfo: PurchaseInfo;
  currency: Currency;
  creditRateInCurrency: number;
  creditsToUse: number;
  creditBalance: number;
}>();

const emits = defineEmits<{
  (event: "onClose"): void;
  (event: "update:creditsToUse", value: number): void;
}>();

const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emits("update:creditsToUse", Number(target.value));
};

onBeforeMount(() => {
  polPrice.refreshRates();
});

const creditAmountInCurrency = computed(() => {
  return formatNumberLocal(
    Number((props.creditsToUse * Number(props.creditRateInCurrency)).toFixed(2))
  );
});

const creditInCurrency = computed(() => {
  return formatNumberLocal(Number((1 * Number(props.creditRateInCurrency)).toFixed(2)));
});

const creditBalanceInCurrency = computed(() =>
  formatNumberLocal(props.creditBalance * props.creditRateInCurrency)
);

const creditsModel = computed({
  get() {
    return props.creditsToUse;
  },
  set(newValue: number) {
    // Ensure the value is a number before emitting
    emits("update:creditsToUse", Number(newValue));
  },
});

const currencySymbol = computed(() => {
  return getCurrencySymbol(props.currency as Currency);
});
</script>

<template>
  <div class="h-[1px] w-full bg-neutral-800"></div>
  <div class="rounded-md p-4 flex flex-col bg-neutral-800">
    <div class="mb-4 justify-between text-sm flex flex-row">
      <div>{{ t("_payment.use-your-credits") }}</div>
      <div class="flex flex-row items-center gap-1 text-gray-200">
        <span class="text-xs"> 1 {{ t("_payment.credit") }} = {{ creditInCurrency }} </span>
        <template v-if="props.currency === Currency.WPOL">
          <icon-fg-w-pol class="w-3 h-3" /></template
        ><template v-else>
          {{ currencySymbol }}
        </template>
      </div>
    </div>
    <div class="flex flex-col items-center gap-2">
      <input
        type="range"
        :min="0"
        :max="creditBalance"
        :step="1"
        v-model="creditsModel"
        @input="handleInputChange"
        class="slider w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:none"
      />
      <div
        class="text-xs text-neutral-500 grid grid-flow-col items-center gap-1 justify-between w-full mt-2"
      >
        <div>0</div>
        <div class="font-semibold text-neutral-200 flex flex-row items-center gap-1">
          <span>{{ creditAmountInCurrency }}</span>
          <template v-if="props.currency === Currency.WPOL">
            <icon-fg-w-pol class="w-3 h-3" /></template
          ><template v-else>
            {{ currencySymbol }}
          </template>
        </div>
        <div>
          {{ creditBalanceInCurrency }}
        </div>
      </div>
    </div>
  </div>

  <div class="rounded-md p-4 flex flex-col bg-neutral-800/60">
    <div class="flex justify-between text-xs text-neutral-400">
      <div>{{ t("_payment.bid-amount") }} :</div>
      <div class="flex flex-row items-center gap-1">
        {{ purchaseInfo.bidAmount }}
        <template v-if="props.currency === Currency.WPOL">
          <icon-fg-w-pol class="w-3 h-3 opacity-50" /></template
        ><template v-else>
          {{ currencySymbol }}
        </template>
      </div>
    </div>
    <div class="flex justify-between text-xs text-neutral-400">
      <div>{{ t("_payment.credits-used") }} :</div>
      <div class="flex flex-row items-center gap-1">
        <span v-if="purchaseInfo.credits.amountInCurrency">-</span
        >{{ parseFloat(purchaseInfo.credits.amountInCurrency.toFixed(2)) }}
        <div>
          <template v-if="props.currency === Currency.WPOL">
            <icon-fg-w-pol class="w-3 h-3 opacity-50" /></template
          ><template v-else>
            {{ currencySymbol }}
          </template>
        </div>
      </div>
    </div>
    <div class="flex justify-between text-base border-t pt-2 mt-2 border-neutral-700">
      <div>{{ t("_payment.final-amount") }} :</div>
      <div class="flex flex-row items-center">
        {{ purchaseInfo.finalAmount }}
        <template v-if="props.currency === Currency.WPOL">
          <icon-fg-w-pol class="w-3 h-3 ml-1" /></template
        ><template v-else>
          {{ currencySymbol }}
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
input {
  @apply border-transparent bg-transparent outline-none w-full;
  box-shadow: none;
  border-radius: 0;
  &::placeholder {
    @apply text-neutral-500;
  }
}

.slider {
  @apply w-full h-0.5 bg-gray-500 rounded-lg appearance-none cursor-pointer;
}

.slider::-webkit-slider-thumb {
  @apply appearance-none w-3 h-3 bg-black rounded-full cursor-pointer ring-2 ring-gray-300 hover:bg-blue-600;
}

.slider::-moz-range-thumb {
  @apply w-5 h-5 bg-blue-500 rounded-full cursor-pointer border-0;
}
</style>
